<template>
  <div>
    <div v-if="selectedField">
      <label class="form-label" :for="fieldId">
        {{ selectedField.label || selectedField.key }}
        <small class="text-danger" v-if="selectedField.required">*</small>
      </label>
      <!-- Campo booleano (switch) -->
      <div v-if="fieldType === 'boolean'" class="form-check form-switch">
        <input
          type="checkbox"
          :checked="currentValue === true"
          :id="fieldId"
          class="form-check-input"
          :class="{ 'bg-primary border-primary': currentValue === true }"
          @change="onFieldChange($event.target.checked)"
        />
        <label :for="fieldId" class="form-check-label">
          {{ currentValue === true ? "YES" : "NO" }}
        </label>
      </div>

      <!-- Campo de texto -->
      <input
        v-else-if="fieldType === 'text'"
        :value="currentValue"
        :id="fieldId"
        class="form-control"
        type="text"
        :placeholder="selectedField.label || selectedField.key"
        @input="onFieldChange($event.target.value)"
      />

      <!-- Campo numérico -->
      <input
        v-else-if="fieldType === 'number'"
        :value="currentValue"
        :id="fieldId"
        class="form-control field-number"
        type="number"
        step="any"
        :placeholder="selectedField.label || selectedField.key"
        @input="onNumberChange($event.target.value)"
      />

      <!-- Campo de lista (con soporte para jerarquía) -->
      <div v-else-if="fieldType === 'list'" class="input-list">
        <!-- List jerárquico: selector jerárquico (prioridad alta) -->
        <HierarchicalSelect
          v-if="hasHierarchicalOptions"
          :value="currentValue"
          :listOptions="listOptions"
          :listType="selectedField.listType"
          :multiple="!!selectedField.isArray"
          :parentLabel="selectedField.parentLabel || 'Categoría'"
          :childLabel="selectedField.childLabel || 'Subcategoría'"
          :parentPlaceholder="selectedField.parentPlaceholder || ''"
          :childPlaceholder="selectedField.childPlaceholder || ''"
          :disabled="false"
          @input="onListChange"
        />

        <!-- List múltiple: multiselect moderno -->
        <div v-else-if="selectedField.listType">
          <multiselect
            :value="currentValue"
            :options="listOptions"
            :multiple="selectedField.isArray"
            :close-on-select="!selectedField.isArray"
            :clear-on-select="true"
            :preserve-search="true"
            :preselect-first="false"
            :allow-empty="true"
            :searchable="true"
            :show-labels="true"
            :taggable="true"
            :create-tag="true"
            label="label"
            track-by="_id"
            :placeholder="
              selectedField.isArray
                ? 'Select one or more options or type to add new'
                : 'Select or type to add new...'
            "
            class="mb-2"
            @input="onListChange"
            @remove="onListChange(null)"
            @tag="onListTagAdded"
          >
            <template #selection="{ values, option }">
              <span v-if="selectedField.isArray">
                <span v-if="values.length === 0" class="multiselect__placeholder"
                  >Select one or more options</span
                >
                <span v-for="(opt, i) in values" :key="i" class="multiselect__tag">
                  {{ opt.label }}
                  <span
                    class="multiselect__tag-icon"
                    @click.stop="onListChange(values.filter((o) => o._id !== opt._id))"
                  ></span>
                </span>
              </span>
              <span v-else>
                <template v-if="option">
                  {{ option.label }}
                  <span
                    class="multiselect__tag-icon"
                    @click.stop="onListChange(null)"
                  ></span>
                </template>
              </span>
            </template>
          </multiselect>
        </div>

        <!-- List simple: multiselect moderno -->
        <multiselect
          v-else
          :value="currentValue"
          :options="listOptions"
          :multiple="selectedField.isArray"
          :close-on-select="!selectedField.isArray"
          :clear-on-select="true"
          :preserve-search="true"
          :preselect-first="false"
          :allow-empty="true"
          :searchable="true"
          :show-labels="true"
          label="label"
          track-by="_id"
          :placeholder="
            selectedField.isArray ? 'Select one or more options' : 'Select an option'
          "
          class="mb-2"
          @input="onListChange"
          @remove="onListChange(null)"
        >
          <template #selection="{ values, option }">
            <span v-if="selectedField.isArray">
              <span v-if="values.length === 0" class="multiselect__placeholder"
                >Select one or more options</span
              >
              <span v-for="(opt, i) in values" :key="i" class="multiselect__tag">
                {{ opt.label }}
                <span
                  class="multiselect__tag-icon"
                  @click.stop="onListChange(values.filter((o) => o._id !== opt._id))"
                ></span>
              </span>
            </span>
            <span v-else>
              <template v-if="option">
                {{ option.label }}
                <span
                  class="multiselect__tag-icon"
                  @click.stop="onListChange(null)"
                ></span>
              </template>
            </span>
          </template>
        </multiselect>
      </div>

      <!-- Campo de referencia (con multiselect y búsqueda) -->
      <div
        v-else-if="fieldType === 'reference' || fieldType === 'user'"
        class="d-flex align-items-center gap-2 reference-input-container"
      >
        <!-- Botón de lupa para abrir modal de búsqueda -->
        <button
          type="button"
          class="btn btn-sm btn-link reference-search-btn"
          @click="openSearchModal"
          title="Buscar en popup"
        >
          <i class="material-icons">search</i>
        </button>

        <!-- Multiselect para campos de referencia -->
        <multiselect
          :value="referenceCurrentValue"
          :options="effectiveReferenceOptions"
          :multiple="selectedField.isArray"
          :close-on-select="!selectedField.isArray"
          :clear-on-select="true"
          :preserve-search="true"
          :preselect-first="false"
          :allow-empty="true"
          :searchable="true"
          :internal-search="false"
          :show-labels="true"
          label="name"
          track-by="_id"
          :placeholder="getReferencePlaceholder()"
          class="mb-2 border-primary border-radius flex-grow-1"
          @input="onReferenceFieldChange"
          @remove="onReferenceFieldChange(null)"
          @search-change="onReferenceSearchChange"
          :custom-label="getMultiselectLabel"
          :loading="isSearchingReference"
        >
          <template #selection="{ values, option }">
            <span v-if="selectedField.isArray">
              <div v-for="(opt, i) in filteredReferenceValues(values)" :key="i">
                <span class="multiselect__tag">
                  {{ getMultiselectLabel(opt) }}
                  <span
                    class="multiselect__tag-icon"
                    @click.stop="
                      onReferenceFieldChange(
                        values.filter((o) => o && o._id !== opt._id)
                      )
                    "
                  ></span>
                </span>
              </div>
            </span>
            <span v-else>
              <template v-if="option">
                {{ getMultiselectLabel(option) }}
                <span
                  class="multiselect__tag-icon"
                  @click.stop="onReferenceFieldChange(null)"
                ></span>
              </template>
            </span>
          </template>
        </multiselect>
      </div>

      <!-- Modal de búsqueda para referencias -->
      <ReferenceSearchModal
        v-if="fieldType === 'reference' || fieldType === 'user'"
        :is-visible="showSearchModal"
        :field="selectedField"
        :current-value="referenceCurrentValue"
        :reference-options="effectiveReferenceOptions"
        @select="onModalSelect"
        @close="showSearchModal = false"
      />

      <!-- Campo de fecha -->
      <input
        v-else-if="fieldType === 'date'"
        :value="formatDateForInput(currentValue)"
        :id="fieldId"
        class="form-control"
        :type="selectedField.time ? 'datetime-local' : 'date'"
        @change="onDateChange($event.target.value)"
      />
    </div>
    <div v-else class="text-center py-4">
      <div class="text-muted">No field selected</div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { wencesApi } from "@/api/wencesApi";
import ReferenceSearchModal from "./ReferenceSearchModal.vue";
import HierarchicalSelect from "./HierarchicalSelect.vue";

export default {
  name: "FormWrapper",
  components: {
    Multiselect,
    ReferenceSearchModal,
    HierarchicalSelect,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    lists: {
      type: [Object, Array],
      default: () => ({}),
    },
    selectedModel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      // Estado para campos de referencia
      showSearchModal: false,
      isSearchingReference: false,
      // Hay un término de búsqueda activo: las opciones ya vienen filtradas, no se cae al listado completo.
      referenceSearchActive: false,
      searchTimeout: null,
      internalReferenceOptions: [],
      localReferenceOptions: [],
      localReferenceOptionsBackup: [],
      // Listas cargadas localmente si no vienen en props
      localLists: [],
      listsLoaded: false,
    };
  },
  computed: {
    selectedField() {
      return this.fields && this.fields.length > 0 ? this.fields[0] : null;
    },
    fieldId() {
      return this.selectedField
        ? `form-wrapper-${this.selectedField.key}`
        : "form-wrapper-field";
    },
    fieldType() {
      if (!this.selectedField) return null;

      // Determinar el tipo de campo
      const type = this.selectedField.type;

      // Si es reference y el ref es 'users', tratarlo como user
      if (type === "reference" && this.selectedField.ref === "users") {
        return "user";
      }

      // Mapear tipos comunes
      if (type === "string" || type === "email" || type === "textarea") {
        return "text";
      }

      return type;
    },
    currentValue() {
      if (!this.selectedField) return null;
      return this.value[this.selectedField.key] ?? null;
    },
    listOptions() {
      if (!this.selectedField || this.fieldType !== "list") return [];

      const listType = this.selectedField.listType;
      if (!listType) return [];

      // Usar lists del prop o las listas locales cargadas
      const listsToSearch = 
        (this.lists && (Array.isArray(this.lists) || Object.keys(this.lists).length > 0))
          ? this.lists 
          : this.localLists;

      // Si lists es un array (formato estándar de /api/gotrading/lists)
      if (Array.isArray(listsToSearch)) {
        const list = listsToSearch.find((l) => l.type === listType);
        if (list && list.values) {
          return list.values;
        }
      }

      // Si lists es un objeto, buscar directamente por listType
      if (listsToSearch && typeof listsToSearch === "object" && !Array.isArray(listsToSearch)) {
        // Si tiene la clave listType directamente
        if (listsToSearch[listType]) {
          const listData = listsToSearch[listType];
          // Si es un array directamente, devolverlo
          if (Array.isArray(listData)) {
            return listData;
          }
          // Si es un objeto con .values, devolver values
          if (listData.values && Array.isArray(listData.values)) {
            return listData.values;
          }
        }
      }

      // Si no hay listas y aún no se han cargado, cargarlas
      if (!this.listsLoaded && (!this.lists || (Array.isArray(this.lists) && this.lists.length === 0) || (typeof this.lists === "object" && Object.keys(this.lists).length === 0))) {
        this.loadLists();
      }

      return [];
    },
    hasHierarchicalOptions() {
      if (!this.listOptions || this.listOptions.length === 0) return false;

      // Verificar si hay elementos con parent (hijos) y elementos sin parent (padres)
      const hasParents = this.listOptions.some((item) => !item.parent);
      const hasChildren = this.listOptions.some((item) => item.parent);

      return hasParents && hasChildren;
    },
    referenceOptions() {
      if (
        !this.selectedField ||
        (this.fieldType !== "reference" && this.fieldType !== "user")
      )
        return [];

      const ref = this.selectedField.ref || (this.fieldType === "user" ? "users" : null);
      if (!ref) return [];

      // Buscar en lists
      if (this.lists && this.lists[ref]) {
        return Array.isArray(this.lists[ref]) ? this.lists[ref] : [];
      }

      return [];
    },
    userOptions() {
      if (!this.selectedField || this.fieldType !== "user") return [];

      // Buscar usuarios en lists
      if (this.lists && this.lists.users) {
        return Array.isArray(this.lists.users) ? this.lists.users : [];
      }

      return [];
    },
    // Valor actual para referencias (formateado para multiselect)
    referenceCurrentValue() {
      if (!this.currentValue) return null;
      return this.currentValue;
    },
    // Opciones efectivas para referencias (con soporte para preload)
    effectiveReferenceOptions() {
      if (!this.selectedField) return [];

      const ref = this.selectedField.ref;
      if (!ref) return [];

      // Si el campo tiene preload habilitado, usar las opciones locales
      if (this.selectedField.preload) {
        if (
          !this.referenceSearchActive &&
          this.localReferenceOptions.length === 0 &&
          this.referenceOptions.length > 0
        ) {
          return this.referenceOptions;
        }
        return this.localReferenceOptions;
      }

      // Si no hay preload, usar las opciones internas
      return this.internalReferenceOptions;
    },
  },
  watch: {
    // Watcher para cargar opciones de referencia cuando cambian
    referenceOptions: {
      handler(newOptions) {
        if (
          this.selectedField &&
          this.selectedField.preload &&
          this.localReferenceOptions.length === 0 &&
          newOptions.length > 0
        ) {
          this.localReferenceOptions = [...newOptions];
          this.localReferenceOptionsBackup = [...newOptions];
        }
      },
      immediate: true,
    },
    // Watcher para inicializar con el valor actual
    currentValue: {
      handler(newValue) {
        if (
          this.selectedField &&
          (this.fieldType === "reference" || this.fieldType === "user") &&
          !this.selectedField.preload &&
          newValue
        ) {
          this.initializeReferenceWithCurrentValue();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Si el campo tiene preload, cargar todas las opciones
    if (
      this.selectedField &&
      (this.fieldType === "reference" || this.fieldType === "user") &&
      this.selectedField.preload
    ) {
      this.loadAllReferenceOptions();
    } else if (
      this.selectedField &&
      (this.fieldType === "reference" || this.fieldType === "user")
    ) {
      this.initializeReferenceWithCurrentValue();
    }

    // Si es un campo tipo list y no hay listas disponibles, cargarlas
    if (
      this.selectedField &&
      this.fieldType === "list" &&
      (!this.lists || (Array.isArray(this.lists) && this.lists.length === 0) || (typeof this.lists === "object" && Object.keys(this.lists).length === 0)) &&
      !this.listsLoaded
    ) {
      this.loadLists();
    }
  },
  methods: {
    onFieldChange(value) {
      if (!this.selectedField) return;

      const newValue = { ...this.value };
      newValue[this.selectedField.key] = value;
      this.$emit("input", newValue);
    },
    getListValue() {
      if (!this.currentValue) return "";

      // Si el valor es un objeto con _id, usar el _id
      if (typeof this.currentValue === "object" && this.currentValue._id) {
        return this.currentValue._id;
      }

      // Si es un string o número, usarlo directamente
      return this.currentValue;
    },
    onListChange(value) {
      // El valor ya viene procesado desde HierarchicalSelect o Multiselect
      this.onFieldChange(value);
    },
    onListTagAdded(tag) {
      // Crear un nuevo objeto con la estructura esperada
      const newTag = {
        _id: tag,
        label: tag,
      };

      // Si es un array, agregar al array existente
      if (this.selectedField.isArray) {
        const currentValue = Array.isArray(this.currentValue) ? this.currentValue : [];
        const newValue = [...currentValue, newTag];
        this.onFieldChange(newValue);
      } else {
        // Si no es array, reemplazar el valor
        this.onFieldChange(newTag);
      }
    },
    // Métodos para campos de referencia
    openSearchModal() {
      this.showSearchModal = true;
    },
    onReferenceFieldChange(value) {
      this.onFieldChange(value);
    },
    onModalSelect(selectedValue) {
      // Procesar el valor seleccionado del modal
      let processedValue = selectedValue;

      if (this.selectedField.isArray && Array.isArray(selectedValue)) {
        processedValue = selectedValue.map((item) => {
          if (typeof item === "string") {
            const found = this.effectiveReferenceOptions.find(
              (opt) => opt._id === item
            );
            const result = found || { _id: item, name: item };
            if (!found) {
              if (this.selectedField.preload) {
                this.localReferenceOptions.push(result);
                this.localReferenceOptionsBackup.push(result);
              } else {
                this.internalReferenceOptions.push(result);
              }
            }
            return result;
          }
          return item;
        });
      } else if (selectedValue) {
        if (typeof selectedValue === "string") {
          const found = this.effectiveReferenceOptions.find(
            (opt) => opt._id === selectedValue
          );
          processedValue = found || { _id: selectedValue, name: selectedValue };
          if (!found) {
            if (this.selectedField.preload) {
              this.localReferenceOptions.push(processedValue);
              this.localReferenceOptionsBackup.push(processedValue);
            } else {
              this.internalReferenceOptions.push(processedValue);
            }
          }
        }
      }

      this.onFieldChange(processedValue);

      if (!this.selectedField.isArray) {
        this.showSearchModal = false;
      }
    },
    getMultiselectLabel(option) {
      if (!option) return "No name";

      if (typeof option === "string") {
        const optionFound = this.effectiveReferenceOptions.find(
          (opt) => opt._id === option
        );
        if (optionFound) {
          return optionFound.name || optionFound.label || String(option);
        }
        return String(option);
      }

      if (option.name) return option.name;
      if (option.label) return option.label;
      if (option._id) return String(option._id);

      return "No name";
    },
    filteredReferenceValues(values) {
      if (!values) return [];
      return values.filter(
        (v) => v && (v._id || v.name || v.label || typeof v === "string")
      );
    },
    getReferencePlaceholder() {
      if (!this.selectedField) return "Seleccione...";

      if (this.selectedField.preload) {
        return this.selectedField.isArray
          ? "Seleccione una o más opciones..."
          : "Seleccione una opción...";
      }
      return this.selectedField.isArray
        ? "Type to search records..."
        : "Type to search a record...";
    },
    async onReferenceSearchChange(searchTerm) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.referenceSearchActive = !!(searchTerm && searchTerm.trim().length >= 2);

      // Si el campo tiene preload, hacer búsqueda local
      if (this.selectedField.preload) {
        if (!searchTerm || searchTerm.trim().length < 2) {
          this.localReferenceOptions = [...this.localReferenceOptionsBackup];
          return;
        }

        const searchTermLower = searchTerm.toLowerCase().trim();
        // Pegar el ID del listado tiene que encontrar el registro (idModel es numérico).
        if (/^\d+$/.test(searchTermLower)) {
          const byId = this.localReferenceOptionsBackup.filter(
            (option) => String(option.idModel) === searchTermLower
          );
          if (byId.length > 0) {
            this.localReferenceOptions = byId;
            return;
          }
        }
        this.localReferenceOptions = this.localReferenceOptionsBackup.filter(
          (option) => {
            if (option.name && option.name.toLowerCase().includes(searchTermLower))
              return true;
            if (option.label && option.label.toLowerCase().includes(searchTermLower))
              return true;
            if (
              option.textPlainVersion &&
              option.textPlainVersion.toLowerCase().includes(searchTermLower)
            )
              return true;
            for (const key in option) {
              if (
                key !== "_id" &&
                key !== "__v" &&
                typeof option[key] === "string" &&
                option[key].toLowerCase().includes(searchTermLower)
              ) {
                return true;
              }
            }
            return false;
          }
        );
        return;
      }

      // Si no hay preload, hacer búsqueda en el servidor
      if (!searchTerm || searchTerm.trim().length < 2) {
        this.initializeReferenceWithCurrentValue();
        return;
      }

      this.searchTimeout = setTimeout(async () => {
        await this.searchReferences(searchTerm);
      }, 300);
    },
    async searchReferences(searchTerm) {
      if (!this.selectedField || !this.selectedField.ref) return;

      this.isSearchingReference = true;

      try {
        const params = new URLSearchParams();
        params.append("textPlainVersion", searchTerm.trim());
        params.append("perPage", "20");

        if (
          this.selectedField.refFilter &&
          this.selectedField.refFilterValue
        ) {
          params.append(
            this.selectedField.refFilter,
            this.selectedField.refFilterValue
          );
        }

        const url = `/api/gotrading/dynamic/${
          this.selectedField.ref
        }?${params.toString()}`;
        const { data } = await wencesApi.get(url);

        const validData = (data.items || data || []).filter(
          (item) => item && (item._id || item.name)
        );

        let finalOptions = [...validData];

        // Agregar el valor actual si no está en los resultados
        if (this.currentValue) {
          const currentId =
            typeof this.currentValue === "string"
              ? this.currentValue
              : this.currentValue._id || this.currentValue;
          const exists = finalOptions.some((opt) => opt._id === currentId);
          if (!exists && currentId) {
            finalOptions.push(
              typeof this.currentValue === "string"
                ? { _id: this.currentValue, name: this.currentValue }
                : this.currentValue
            );
          }
        }

        this.internalReferenceOptions = finalOptions;
      } catch (error) {
        console.error("Error searching references:", error);
        this.internalReferenceOptions = [];
      } finally {
        this.isSearchingReference = false;
      }
    },
    async loadAllReferenceOptions() {
      if (
        !this.selectedField ||
        !this.selectedField.preload ||
        !this.selectedField.ref
      ) {
        return;
      }

      try {
        const params = new URLSearchParams();
        params.append("perPage", "1000");
        params.append("fields", "_id,name,label,textPlainVersion");

        if (
          this.selectedField.refFilter &&
          this.selectedField.refFilterValue
        ) {
          params.append(
            this.selectedField.refFilter,
            this.selectedField.refFilterValue
          );
        }

        const url = `/api/gotrading/dynamic/${
          this.selectedField.ref
        }?${params.toString()}`;
        const { data } = await wencesApi.get(url);

        const validData = (data.items || data || []).filter(
          (item) => item && (item._id || item.name)
        );

        let finalOptions = [...validData];

        // Agregar el valor actual si no está en los resultados
        if (this.currentValue) {
          const currentId =
            typeof this.currentValue === "string"
              ? this.currentValue
              : this.currentValue._id || this.currentValue;
          const exists = finalOptions.some((opt) => opt._id === currentId);
          if (!exists && currentId) {
            finalOptions.push(
              typeof this.currentValue === "string"
                ? { _id: this.currentValue, name: this.currentValue }
                : this.currentValue
            );
          }
        }

        this.localReferenceOptions = finalOptions;
        this.localReferenceOptionsBackup = [...finalOptions];
      } catch (error) {
        console.error("Error loading all reference options:", error);
        this.localReferenceOptions = this.referenceOptions;
      }
    },
    initializeReferenceWithCurrentValue() {
      if (!this.currentValue) {
        return;
      }

      const optionsFromValue = [];

      if (this.selectedField.isArray && Array.isArray(this.currentValue)) {
        this.currentValue.forEach((item) => {
          if (item) {
            optionsFromValue.push(
              typeof item === "string"
                ? { _id: item, name: item }
                : item
            );
          }
        });
      } else if (this.currentValue) {
        optionsFromValue.push(
          typeof this.currentValue === "string"
            ? { _id: this.currentValue, name: this.currentValue }
            : this.currentValue
        );
      }

      this.internalReferenceOptions = optionsFromValue;
    },
    formatDateForInput(value) {
      if (!value) return "";

      // Si ya es una cadena en formato YYYY-MM-DD, devolverla
      if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)) {
        // Si tiene hora, extraer solo la parte de fecha si es date sin time
        if (!this.selectedField.time && value.includes("T")) {
          return value.split("T")[0];
        }
        return value;
      }

      // Si es un objeto Date, convertirlo
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        let result = `${year}-${month}-${day}`;

        if (this.selectedField.time) {
          const hours = String(value.getHours()).padStart(2, "0");
          const minutes = String(value.getMinutes()).padStart(2, "0");
          result += `T${hours}:${minutes}`;
        }

        return result;
      }

      return "";
    },
    onDateChange(value) {
      this.onFieldChange(value);
    },
    onNumberChange(value) {
      // Convertir a número si es posible, o mantener el valor original
      const numValue = value === "" ? null : Number(value);
      this.onFieldChange(isNaN(numValue) ? value : numValue);
    },
    async loadLists() {
      if (this.listsLoaded) return;

      try {
        this.listsLoaded = true;
        const { data } = await wencesApi.get("/api/gotrading/lists");
        this.localLists = data;
      } catch (error) {
        console.error("[FormWrapper] Error loading lists:", error);
        this.listsLoaded = false;
      }
    },
  },
};
</script>

<style scoped>
.form-control {
  padding: 0 7px !important;
}

.form-select {
  padding: 0 7px !important;
}

.form-check-input {
  margin-top: 0.25rem;
}

.reference-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reference-search-btn {
  min-width: auto !important;
  height: auto !important;
  color: #6c757d !important;
  text-decoration: none !important;
  border: none !important;
  background: transparent !important;
  cursor: pointer;
  flex-shrink: 0;
}

.reference-search-btn:hover {
  color: var(--bs-primary) !important;
  background: transparent !important;
}

.reference-search-btn .material-icons {
  font-size: 20px;
}

.border-primary {
  border-color: #007bff !important;
}

.border-radius {
  border-radius: 0.375rem !important;
}
</style>

<style>
/* Estilos para multiselect */
:deep(.multiselect-container) {
  position: relative;
  z-index: 1;
}

:deep(.multiselect.is-active) {
  z-index: 99999;
}

:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}
</style>
